import { Button, Group, Stack, Text } from "@mantine/core";
import { IconHome, IconSolarPanel2 } from "@tabler/icons-react";
import { useEffect } from "react";
import {
  Controller,
  type Control,
  type ControllerRenderProps,
  type FieldErrors,
  type UseFormGetValues,
  type UseFormSetValue,
  type UseFormWatch
} from "react-hook-form";
import { useToast } from "../../../../mantine/notifications/useToast";
import {
  MeteringDirection,
  SiteSetupProcessDataMieterstromzuschlag_application_intent,
  SupplyType
} from "../../../../types/api.types";
import { getErrorText } from "../../../../utils/get-error-text";
import { FormFieldController } from "../../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { useSiteSetupAssistantMutations } from "../../hooks/useSiteSetupAssistantMutations";
import { type SiteSetupProcessForForm } from "../../SiteSetupAssistant.types";
import { formatFrontendDataToBackend } from "../../utils/formatFrontendDataToBackend";
import { SITE_SETUP_ASSISTANT_FORM } from "../SiteSetupAssistantForms.constants";

const zipRegex = /^\d{5}$/;

interface AddressFormItemsProps {
  formControl: Control<SiteSetupProcessForForm>;
  formErrors: FieldErrors<SiteSetupProcessForForm>;
  getValues: UseFormGetValues<SiteSetupProcessForForm>;
  siteSetupProcessId: string | undefined;
  watch: UseFormWatch<SiteSetupProcessForForm>;
  onBlockNavigation: (blocked: boolean) => void;
  onSetFormValue: UseFormSetValue<SiteSetupProcessForForm>;
}

function AddressFormItems({
  formControl,
  formErrors,
  siteSetupProcessId,
  getValues,
  watch,
  onBlockNavigation,
  onSetFormValue
}: AddressFormItemsProps) {
  const { showErrorToast } = useToast();
  const { updateMutation: eagerSaveMutation } = useSiteSetupAssistantMutations(
    siteSetupProcessId,
    { updateCache: false }
  );

  const connectionMeteringDirection = watch("connectionMeteringDirection");
  const anyPvPlantEligible = watch("anyPvPlantEligibleForMieterstromzuschlag");
  const watchedIntent = watch("mieterstromzuschlagApplicationIntent");

  useEffect(() => {
    if (
      watchedIntent !==
      SiteSetupProcessDataMieterstromzuschlag_application_intent.ApplyOrAlreadyApplied
    ) {
      onSetFormValue("mieterstromzuschlagApplicationDate", null);
    }
  }, [watchedIntent, onSetFormValue]);

  useEffect(() => {
    const isBlocked = eagerSaveMutation.isPending;
    onBlockNavigation(isBlocked);
  }, [eagerSaveMutation.isPending, onBlockNavigation]);

  useEffect(() => {
    return () => onBlockNavigation(false);
  }, [onBlockNavigation]);

  async function handleSupplyTypeChange(
    newType: SupplyType,
    field: ControllerRenderProps<SiteSetupProcessForForm, "siteSupplyType">
  ) {
    if (newType === SupplyType.FullSupply) {
      if (connectionMeteringDirection === null) {
        onSetFormValue(
          "connectionMeteringDirection",
          MeteringDirection.Zweirichtungszaehler
        );
      }
    } else if (newType === SupplyType.PartialSupply) {
      if (connectionMeteringDirection === null) {
        onSetFormValue(
          "connectionMeteringDirection",
          MeteringDirection.Erzeugung_Einspeisung
        );
      }
    }

    field.onChange(newType);

    try {
      const payload = formatFrontendDataToBackend({
        ...getValues(),
        siteSupplyType: newType
      });
      const response = await eagerSaveMutation.mutateAsync(payload);
      const isEligible =
        response.any_pv_plant_eligible_for_mieterstromzuschlag ?? null;

      onSetFormValue("anyPvPlantEligibleForMieterstromzuschlag", isEligible);

      if (!isEligible) {
        onSetFormValue("mieterstromzuschlagApplicationIntent", null);
        onSetFormValue("mieterstromzuschlagApplicationDate", null);
      }
    } catch (error) {
      showErrorToast(getErrorText(error));
    }
  }

  return (
    <div className="AddressFormItems site-setup-assistant-form">
      <Text fw={500} mb="sm">
        Adresse der Liegenschaft
      </Text>
      <FormFieldController
        control={formControl}
        data={SITE_SETUP_ASSISTANT_FORM.siteAddress}
        error={formErrors.siteAddress}
      />
      <Group>
        <FormFieldController
          control={formControl}
          data={SITE_SETUP_ASSISTANT_FORM.siteZip}
          error={formErrors.siteZip}
          rules={{
            validate: (value) =>
              !value ||
              zipRegex.test((value || "").toString()) ||
              "Bitte geben Sie eine Postleitzahl bestehend aus fünf Ziffern an."
          }}
        />
        <FormFieldController
          control={formControl}
          data={SITE_SETUP_ASSISTANT_FORM.siteCity}
          error={formErrors.siteCity}
        />
      </Group>

      <Text fw={500} mb="md" mt="lg">
        Versorgungskonzept
      </Text>
      <Controller
        control={formControl}
        name="siteSupplyType"
        render={({ field }) => {
          return (
            <>
              <Text mb="md">
                Welches Versorgungskonzept streben Sie in Ihrer Liegenschaft
                an?*
              </Text>
              <Group gap="lg" justify="center">
                <Button
                  color="primary"
                  h="fit-content"
                  p="lg"
                  variant={
                    field.value === SupplyType.FullSupply
                      ? "outline"
                      : "default"
                  }
                  w={300}
                  onClick={() =>
                    handleSupplyTypeChange(SupplyType.FullSupply, field)
                  }
                >
                  <Stack align="center" gap="none">
                    <IconHome color="black" size="3em" strokeWidth={1.5} />
                    Vollversorgung (Netz- und PV-Strom)
                  </Stack>
                </Button>
                <Button
                  color="primary"
                  h="fit-content"
                  p="lg"
                  variant={
                    field.value === SupplyType.PartialSupply
                      ? "outline"
                      : "default"
                  }
                  w={300}
                  onClick={() =>
                    handleSupplyTypeChange(SupplyType.PartialSupply, field)
                  }
                >
                  <Stack align="center" gap="none">
                    <IconSolarPanel2
                      color="#ffb822"
                      size="3em"
                      strokeWidth={1.5}
                    />
                    Teilversorgung (nur PV-Strom)
                  </Stack>
                </Button>
              </Group>
            </>
          );
        }}
      />

      {anyPvPlantEligible === true && (
        <Stack mt="lg">
          <Text fw={500}>Mieterstromzuschlag</Text>
          <FormFieldController
            control={formControl}
            data={
              SITE_SETUP_ASSISTANT_FORM.mieterstromzuschlagApplicationIntent
            }
            error={formErrors.mieterstromzuschlagApplicationIntent}
          />
          {watchedIntent ===
            SiteSetupProcessDataMieterstromzuschlag_application_intent.ApplyOrAlreadyApplied && (
            <FormFieldController
              control={formControl}
              data={
                SITE_SETUP_ASSISTANT_FORM.mieterstromzuschlagApplicationDate
              }
              error={formErrors.mieterstromzuschlagApplicationDate}
            />
          )}
        </Stack>
      )}
    </div>
  );
}

export { AddressFormItems };
